import React, { useState, useMemo, useEffect } from 'react';
import {
  View, Text, Modal, TouchableOpacity, TextInput, ScrollView,
  ActivityIndicator, StyleSheet, Platform, KeyboardAvoidingView,
  useWindowDimensions,
} from 'react-native';
import { Category, Equipment, Store, StoreInventory } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import CrossPlatformAlert from './CrossPlatformAlert';
import DateTimePickerInput from './DateTimePickerInput';
import { Ionicons } from '@expo/vector-icons';
import { tracksDates, fefoAllocations } from '../utils/inventoryDates';
import StoreBatchSettings from './StoreBatchSettings';
import InventoryBatchPanel, { BatchSelection, emptySelection } from './InventoryBatchPanel';
import { CountRow } from '../types/operations';
import { Dashboard, dashboard, getDraft, newRequestId, operation, taskAction } from '../services/operationsService';
import * as Sentry from '@sentry/react-native';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UsageType = 'take' | 'add' | 'transfer' | 'stocktaking';

interface WizardData {
  date: string;           // DD.MM.YYYY
  time: string;           // HH:MM
  usageType: UsageType;
  fromStoreId: string;    // used by take and transfer
  toStoreId: string;      // used by add, transfer, and stocktaking
  actionNumber: string;
  description: string;
  selectedCategoryIds: string[];
  quantities: Record<string, number>; // equipmentId -> quantity
}

interface SaveMeta {
  type: UsageType;
  fromStoreId?: string;
  toStoreId?: string;
  actionNumber?: string;
  description?: string;
  actionDate: Date;
}

interface Props {
  visible: boolean;
  taskId?: string;
  initialStoreId?: string;
  initialType?: UsageType;
  initialEquipmentId?: string;
  onSaved?: () => void;
  onClose: () => void;
  onSave: (items: { equipmentId: string; quantity: number; itemType?: Equipment['itemType'] }[], meta: SaveMeta) => Promise<void>;
  categories: Category[];
  equipment: Equipment[];
  stores: Store[];
  storeInventory: StoreInventory[];
  isAdminOrManager?: boolean;
  isAdmin?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function padTwo(n: number): string {
  return n.toString().padStart(2, '0');
}

function getInitialData(): WizardData {
  const now = new Date();
  return {
    date: `${padTwo(now.getDate())}.${padTwo(now.getMonth() + 1)}.${now.getFullYear()}`,
    time: `${padTwo(now.getHours())}:${padTwo(now.getMinutes())}`,
    usageType: 'take',
    fromStoreId: '',
    toStoreId: '',
    actionNumber: '',
    description: '',
    selectedCategoryIds: [],
    quantities: {},
  };
}

function parseDateTime(date: string, time: string): Date | null {
  const dp = date.split('.');
  const tp = time.split(':');
  if (dp.length !== 3 || tp.length !== 2) return null;
  const [day, month, year] = dp.map(Number);
  const [hours, minutes] = tp.map(Number);
  if ([day, month, year, hours, minutes].some(isNaN)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  const d = new Date(year, month - 1, day, hours, minutes);
  return isNaN(d.getTime()) ? null : d;
}

/** Validates that action number follows INT/INT/INT format (e.g. 12/05/2026). */
function matchesActionNumberFormat(value: string): boolean {
  return /^\d+\/\d+\/\d+$/.test(value.trim());
}

/** Returns all category IDs that are descendants of catId (including catId itself). */
function getDescendantIds(catId: string, allCategories: Category[]): string[] {
  const children = allCategories.filter(c => c.parentId === catId).map(c => c.id);
  return [catId, ...children.flatMap(id => getDescendantIds(id, allCategories))];
}

/**
 * Returns true when any category in the equipment's ancestor chain (from its
 * direct category upward) has requiresActionNumber === true.
 * Used to lock individual equipment rows in the TAKE wizard step.
 */
function isCategoryChainLocked(categoryId: string, allCategories: Category[]): boolean {
  let cat: Category | undefined = allCategories.find(c => c.id === categoryId);
  while (cat) {
    if (cat.requiresActionNumber) return true;
    cat = cat.parentId ? allCategories.find(c => c.id === cat!.parentId) : undefined;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function NewUsageWizard({
  visible, onClose, onSave, categories, equipment: allEquipment, stores, storeInventory, isAdminOrManager,
  taskId, initialStoreId, initialType, initialEquipmentId, onSaved, isAdmin,
}: Props) {
  const { t } = useLanguage();
  const { width: windowWidth } = useWindowDimensions();

  // Date range: today (no future) and 30 days ago (no older).
  // Computed fresh on each render so they're always accurate.
  const maxDate = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();
  const minDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const [data, setData] = useState<WizardData>(getInitialData);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'full' | 'categorized'>('full');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const [batchData, setBatchData] = useState<Dashboard | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [selection, setSelection] = useState<Record<string, BatchSelection>>({});
  const [taskRows, setTaskRows] = useState<CountRow[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState('');
  const [requestId, setRequestId] = useState(newRequestId);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const equipment = taskId ? allEquipment.filter(e => taskRows.some(r => r.equipmentId === e.id)) : allEquipment;
  const currentStoreId = data.usageType === 'take' || data.usageType === 'transfer' ? data.fromStoreId : data.toStoreId;
  const batchManaged = stores.find(s => s.id === currentStoreId)?.inventoryVersion === 2 || batchData?.stores.find(s => s.id === currentStoreId)?.inventoryVersion === 2 || !!taskId;
  useEffect(() => {
    if (taskId) return;
    if (!visible || !currentStoreId) { setBatchData(null); return; }
    let active = true;
    setBatchLoading(true); setBatchError(''); setBatchData(null); setSelection({});
    dashboard(currentStoreId).then(d => { if (active) setBatchData(d); })
      .catch(e => { if (active) setBatchError(String(e)); })
      .finally(() => { if (active) setBatchLoading(false); });
    return () => { active = false; };
  }, [visible, currentStoreId, data.usageType, taskId]);
  useEffect(() => {
    if (!visible || !taskId) return;
    let active = true;
    setTaskLoading(true); setTaskError('');
    (async () => {
      const d = await dashboard();
      const task = d.tasks.find(t => t.id === taskId && t.type === 'stocktaking');
      if (!task) throw new Error('TASK_NOT_FOUND');
      await taskAction(taskId, 'start');
      const [{ rows }, stockData] = await Promise.all([getDraft(taskId), dashboard(task.storeId)]);
      if (!active) return;
      setTaskRows(rows); setBatchData(stockData);
      setData(prev => ({ ...prev, usageType: 'stocktaking', toStoreId: task.storeId,
        description: task.description || task.title,
        selectedCategoryIds: categories.filter(c => !c.parentId).filter(c => rows.some(r => allEquipment.some(e => e.id === r.equipmentId && getDescendantIds(c.id, categories).includes(e.categoryId)))).map(c => c.id),
        quantities: Object.fromEntries(rows.map(r => [r.equipmentId, r.batches.reduce((n, b) => n + b.quantity, 0)])),
      }));
      setCheckedItems(new Set(rows.filter(r => r.checked).map(r => r.equipmentId)));
      setSelection(Object.fromEntries(rows.map(r => [r.equipmentId, { ...emptySelection(), lots: r.batches }])));
    })().catch(e => { if (active) setTaskError(String(e)); })
      .finally(() => { if (active) setTaskLoading(false); });
    return () => { active = false; };
  }, [visible, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset every time the wizard opens
  useEffect(() => {
    if (visible) {
      const initial = getInitialData();
      const category = allEquipment.find(e => e.id === initialEquipmentId)?.categoryId;
      initial.usageType = initialType ?? 'take';
      if (initialStoreId) { initial.fromStoreId = initialStoreId; initial.toStoreId = initialStoreId; }
      if (category) initial.selectedCategoryIds = categories.filter(c => !c.parentId && getDescendantIds(c.id, categories).includes(category)).map(c => c.id);
      setData(initial);
      setSelection({}); setTaskRows([]); setRequestId(newRequestId()); setExpandedDetails(new Set());
      setStep(0);
      setSearchQuery('');
      setViewMode('full');
      setCheckedItems(new Set(initialEquipmentId && initialType === 'stocktaking' ? [initialEquipmentId] : []));
    }
  }, [visible, initialStoreId, initialType, initialEquipmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derived data ----

  /** Root-level categories (no parent). */
  const rootCategories = useMemo(
    () => categories.filter(c => !c.parentId && (!taskId || equipment.some(e => getDescendantIds(c.id, categories).includes(e.categoryId)))),
    [categories, equipment, taskId],
  );

  /** Root categories the user has checked. */
  const selectedCategories = useMemo(
    () => rootCategories.filter(c => data.selectedCategoryIds.includes(c.id)),
    [rootCategories, data.selectedCategoryIds],
  );

  /** Whether any root category requires an action number (used for info notice). */
  const anyRootCategoryRequiresActionNumber = useMemo(
    () => rootCategories.some(c => c.requiresActionNumber),
    [rootCategories],
  );

  /** Whether the current action number input is valid (non-empty and matches INT/INT/INT). */
  const hasValidActionNumber = data.actionNumber.trim() !== '' && matchesActionNumberFormat(data.actionNumber);

  // Step layout:
  //   0            = Basic info (date, time, type, stores, action#, desc)
  //   1            = Category selection
  //   2 .. 1+N     = Per-category equipment steps
  //   2+N          = Review (pre-final)
  //   3+N          = Summary (final)
  const catStepStart = 2;
  const reviewStep = catStepStart + selectedCategories.length;
  const summaryStep = reviewStep + 1;
  const totalSteps = summaryStep + 1;

  /** Category shown on the current equipment step (if applicable). */
  const currentCategoryIndex =
    step >= catStepStart && step < reviewStep ? step - catStepStart : -1;
  const currentCategory =
    currentCategoryIndex >= 0 ? selectedCategories[currentCategoryIndex] : null;

  /** Equipment items that belong to the current category (incl. sub-categories). */
  const currentEquipment = useMemo(() => {
    if (!currentCategory) return [];
    const ids = getDescendantIds(currentCategory.id, categories);
    return equipment
      .filter(e => ids.includes(e.categoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentCategory, categories, equipment]);

  /** All items with a quantity > 0, sorted by name. */
  const usedItems = useMemo(() => {
    return Object.entries(data.quantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const eq = equipment.find(e => e.id === id);
        return eq ? { equipment: eq, quantity: qty } : null;
      })
      .filter((x): x is { equipment: Equipment; quantity: number } => x !== null)
      .sort((a, b) => a.equipment.name.localeCompare(b.equipment.name));
  }, [data.quantities, equipment]);

  /** Checked items for stocktaking (any quantity including 0), sorted by name. */
  const stocktakingItems = useMemo(() => {
    return Array.from(checkedItems)
      .map(id => {
        const eq = equipment.find(e => e.id === id);
        const qty = data.quantities[id] ?? storeInventory.find(si => si.storeId === data.toStoreId && si.equipmentId === id)?.quantity ?? 0;
        return eq ? { equipment: eq, quantity: qty } : null;
      })
      .filter((x): x is { equipment: Equipment; quantity: number } => x !== null)
      .sort((a, b) => a.equipment.name.localeCompare(b.equipment.name));
  }, [checkedItems, data.quantities, data.toStoreId, equipment, storeInventory]);

  /** Equipment matching the search query that have not yet been added. */
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return equipment
      .filter(e => {
        const alreadyAdded = data.usageType === 'stocktaking'
          ? checkedItems.has(e.id)
          : data.quantities[e.id] > 0;
        return e.name.toLowerCase().includes(q) && !alreadyAdded;
      })
      .slice(0, 10);
  }, [searchQuery, data.quantities, data.usageType, checkedItems, equipment]);

  // ---- Quantity helpers ----

  const adjustQty = (eqId: string, delta: number, maxQty?: number, baseQty?: number) => {
    setQtyFromInput(eqId, String(Math.max(0, (data.quantities[eqId] ?? baseQty ?? 0) + delta)), maxQty);
  };

  const setQtyFromInput = (eqId: string, raw: string, maxQty?: number, fromDates = false) => {
    // Strip any non-digit characters to allow only positive integer input
    const cleaned = raw.replace(/[^0-9]/g, '');
    const n = parseInt(cleaned, 10) || 0;
    const reusableUse = data.usageType === 'take' && allEquipment.find(item => item.id === eqId)?.itemType === 'reusable';
    const capped = Math.min(n, maxQty ?? Infinity, reusableUse ? 1 : Infinity);
    if (!fromDates && !expandedDetails.has(eqId) && batchManaged) {
      if (data.usageType === 'add' || data.usageType === 'stocktaking') {
        setSelection(prev => ({ ...prev, [eqId]: { ...(prev[eqId] ?? emptySelection()), initialized: true, lots: [{ id: newRequestId(), storeId: currentStoreId, equipmentId: eqId, quantity: capped, expiresOn: null, reviewOn: null }] } }));
      } else setSelection(prev => ({ ...prev, [eqId]: { ...(prev[eqId] ?? emptySelection()), mode: 'fefo', allocations: [] } }));
    }
    setData(prev => ({
      ...prev,
      quantities: { ...prev.quantities, [eqId]: capped },
    }));
    // Auto-check item in stocktaking mode when quantity is set via input
    if (data.usageType === 'stocktaking') {
      setCheckedItems(prev => {
        const next = new Set(prev);
        next.add(eqId);
        return next;
      });
    }
  };

  const toggleCheckedItem = (eqId: string) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      if (next.has(eqId)) {
        next.delete(eqId);
      } else {
        next.add(eqId);
      }
      return next;
    });
  };

  const toggleCategory = (catId: string) => {
    setData(prev => {
      const selected = prev.selectedCategoryIds.includes(catId);
      return {
        ...prev,
        selectedCategoryIds: selected
          ? prev.selectedCategoryIds.filter(id => id !== catId)
          : [...prev.selectedCategoryIds, catId],
      };
    });
  };

  // ---- Validation ----

  const step0Valid = () => {
    const dt = parseDateTime(data.date, data.time);
    if (!dt) return false;
    if (dt > maxDate || dt < minDate) return false;
    if (data.usageType === 'take' && !data.fromStoreId) return false;
    if (data.usageType === 'add' && !data.toStoreId) return false;
    if (data.usageType === 'transfer' && (!data.fromStoreId || !data.toStoreId || data.fromStoreId === data.toStoreId)) return false;
    if (data.usageType === 'stocktaking' && !data.toStoreId) return false;
    return true;
  };

  // ---- Navigation ----

  const handleNext = async () => {
    if (step === 0) {
      if (!parseDateTime(data.date, data.time)) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardInvalidDate'));
        return;
      }
      const dt = parseDateTime(data.date, data.time)!;
      if (dt > maxDate || dt < minDate) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardDateOutOfRange'));
        return;
      }
      if (data.usageType === 'take' && !data.fromStoreId) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardFromStoreRequired'));
        return;
      }
      if (data.usageType === 'add' && !data.toStoreId) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardToStoreRequired'));
        return;
      }
      if (data.usageType === 'transfer' && (!data.fromStoreId || !data.toStoreId)) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardBothStoresRequired'));
        return;
      }
      if (data.usageType === 'stocktaking' && !data.toStoreId) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardToStoreRequired'));
        return;
      }
      if (data.actionNumber && !matchesActionNumberFormat(data.actionNumber)) {
        CrossPlatformAlert.alert(t('common.validation'), t('usage.wizardActionNumberFormatError'));
        return;
      }
      setStep(1);
      return;
    }
    if (step === summaryStep) {
      await handleSave();
      return;
    }
    if (step === reviewStep) {
      const sourceStoreId =
        (data.usageType === 'take' || data.usageType === 'transfer') && data.fromStoreId
          ? data.fromStoreId
          : null;
      if (sourceStoreId) {
        let exceededItem: { equipment: Equipment; quantity: number } | undefined;
        let exceededStock = 0;
        for (const item of usedItems) {
          const stockEntry = storeInventory.find(
            si => si.storeId === sourceStoreId && si.equipmentId === item.equipment.id,
          );
          const available = stockEntry?.quantity ?? 0;
          if (item.quantity > available) {
            exceededItem = item;
            exceededStock = available;
            break;
          }
        }
        if (exceededItem) {
          CrossPlatformAlert.alert(
            t('common.validation'),
            t('usage.wizardExceedsStock').replace('%s', `${exceededItem.equipment.name}: ${exceededStock} ${exceededItem.equipment.unit}`),
          );
          return;
        }
      }
    }
    setStep(s => s + 1);
  };

  const handlePrev = () => setStep(s => Math.max(0, s - 1));

  const handleSave = async () => {
    const actionDate = parseDateTime(data.date, data.time)!;
    const itemsToSave: { equipmentId: string; quantity: number; itemType: NonNullable<Equipment['itemType']> }[] = data.usageType === 'stocktaking'
      ? stocktakingItems.map(i => ({ equipmentId: i.equipment.id, quantity: i.quantity, itemType: i.equipment.itemType ?? 'disposable' }))
      : usedItems.map(i => ({ equipmentId: i.equipment.id, quantity: i.quantity, itemType: i.equipment.itemType ?? 'disposable' }));
    setSaving(true);
    try {
      if (batchManaged) {
        if (batchLoading || taskLoading || batchError || taskError || !batchData) throw new Error(t('operations.loading'));
        if (!itemsToSave.length) throw new Error(t('operations.missing'));
        if (data.usageType === 'add' && itemsToSave.some(item => {
          const lots = selection[item.equipmentId]?.lots;
          return lots?.length && lots.reduce((n, b) => n + b.quantity, 0) !== item.quantity;
        })) throw new Error(t('operations.batchTotalMismatch'));
        if (data.usageType === 'stocktaking') {
          const rows: CountRow[] = itemsToSave.map(item => {
            const eq = allEquipment.find(e => e.id === item.equipmentId);
            const batches = eq && tracksDates(eq)
              ? selection[item.equipmentId]?.initialized ? selection[item.equipmentId].lots : batchData.batches.filter(b => b.equipmentId === item.equipmentId)
              : [{ id: newRequestId(), storeId: currentStoreId, equipmentId: item.equipmentId, quantity: item.quantity, expiresOn: null, reviewOn: null }];
            if (batches.reduce((n, b) => n + b.quantity, 0) !== item.quantity) throw new Error(t('operations.batchTotalMismatch'));
            return { equipmentId: item.equipmentId, checked: true, batches,
              version: taskRows.find(r => r.equipmentId === item.equipmentId)?.version ?? batchData.positions.find(p => p.equipmentId === item.equipmentId)?.version ?? 'absent' };
          });
          if (taskId) {
            if (rows.length !== taskRows.length) throw new Error(t('operations.checkAll'));
            await taskAction(taskId, 'draft', { rows });
            await taskAction(taskId, 'complete', { result: data.description || t('usage.typeStocktaking'), actionDate: actionDate.toISOString(), actionNumber: data.actionNumber });
          } else await operation('stocktakingOperation', { storeId: currentStoreId, requestId, rows, result: data.description || t('usage.typeStocktaking'), actionDate: actionDate.toISOString(), actionNumber: data.actionNumber });
        } else {
          await operation('inventoryOperation', { action: 'moveSession', requestId,
            storeId: currentStoreId, toStoreId: data.usageType === 'transfer' ? data.toStoreId : null,
            type: data.usageType, actionDate: actionDate.toISOString(), actionNumber: data.actionNumber,
            reason: data.description || t(`usage.type${data.usageType[0].toUpperCase()}${data.usageType.slice(1)}`),
            items: itemsToSave.map(item => {
              const picked = selection[item.equipmentId] ?? emptySelection();
              return { ...item, ...picked, lots: picked.initialized ? picked.lots : [{ quantity: item.quantity, expiresOn: null, reviewOn: null }] };
            }),
          });
        }
        onSaved?.();
      } else await onSave(itemsToSave, {
        type: data.usageType,
        fromStoreId: (data.usageType === 'take' || data.usageType === 'transfer')
          ? data.fromStoreId : undefined,
        toStoreId: (data.usageType === 'add' || data.usageType === 'transfer' || data.usageType === 'stocktaking')
          ? data.toStoreId : undefined,
        actionNumber: data.actionNumber || undefined,
        description: data.description || undefined,
        actionDate,
      });
      onClose();
      CrossPlatformAlert.alert(t('usage.recordCreated'), t('usage.recordCreatedMessage'));
    } catch (err: any) {
      Sentry.captureException(err);
      const errors: Record<string, string> = { ITEM_TYPE_CHANGED: 'itemTypeChanged', INVALID_REUSABLE_USE: 'invalidReusableUse', STOCKTAKING_CONFLICT: 'conflict', INVALID_ALLOCATION: 'selectBatch', INVALID_LOTS: 'batchTotalMismatch', INSUFFICIENT_STOCK: 'stockLow', CHECK_ALL_ITEMS: 'checkAll', SPLIT_STOCKTAKING: 'split', INITIALIZE_STORE: 'initializeFirst', ACTION_NUMBER_REQUIRED: 'actionRequired', MANAGER_REQUIRED: 'noAccess' };
      const key = Object.keys(errors).find(code => String(err.message).includes(code));
      CrossPlatformAlert.alert(t('common.error'), key ? t(`operations.${errors[key]}`) : err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveTaskDraft = async () => {
    if (!taskId) return;
    const rows = taskRows.map(row => ({ ...row, checked: checkedItems.has(row.equipmentId),
      batches: selection[row.equipmentId]?.initialized ? selection[row.equipmentId].lots : row.batches }));
    for (const row of rows) {
      if (row.batches.reduce((n, b) => n + b.quantity, 0) !== (data.quantities[row.equipmentId] ?? 0))
        throw new Error(t('operations.batchTotalMismatch'));
    }
    await taskAction(taskId, 'draft', { rows });
  };
  const handleSaveDraft = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveTaskDraft();
      CrossPlatformAlert.alert(t('operations.saved'), t('operations.saved'));
    } catch (e) { CrossPlatformAlert.alert(t('common.error'), String(e)); }
    finally { setSaving(false); }
  };

  // ---- Step title ----

  const getStepTitle = (): string => {
    if (step === 0) return t('usage.wizardStepInfo');
    if (step === 1) return t('usage.wizardStepCategories');
    if (step >= catStepStart && step < reviewStep)
      return `${t('usage.wizardStepEquipment')}: ${currentCategory?.name ?? ''}`;
    if (step === reviewStep) return t('usage.wizardStepReview');
    return t('usage.wizardStepSummary');
  };

  // ---- Reusable quantity row ----

  const renderQtyRow = (eq: Equipment, locked?: boolean) => {
    const qty = data.quantities[eq.id] ?? 0;
    const reusableUse = data.usageType === 'take' && eq.itemType === 'reusable';

    const sourceStoreId =
      (data.usageType === 'take' || data.usageType === 'transfer') && data.fromStoreId
        ? data.fromStoreId
        : null;
    const stockEntry = sourceStoreId
      ? storeInventory.find(
          si => si.storeId === sourceStoreId && si.equipmentId === eq.id,
        )
      : null;
    const stockQty = stockEntry?.quantity ?? 0;

    // For stocktaking, show the current store stock as a reference
    const referenceStoreId = data.usageType === 'stocktaking' && data.toStoreId
      ? data.toStoreId : null;
    const referenceEntry = referenceStoreId
      ? storeInventory.find(si => si.storeId === referenceStoreId && si.equipmentId === eq.id)
      : null;

    const isChecked = checkedItems.has(eq.id);
    const hasDates = batchManaged && tracksDates(eq) && !reusableUse;
    const detailsOpen = hasDates && expandedDetails.has(eq.id);

    // In stocktaking mode, use the current store stock as the default displayed value
    // when the user hasn't manually changed the quantity yet.
    const referenceQty = referenceEntry?.quantity ?? 0;
    const stocktakingDisplayQty = data.usageType === 'stocktaking' && data.quantities[eq.id] === undefined
      ? referenceQty
      : qty;

    if (locked) {
      return (
        <View key={eq.id} style={[styles.qtyRow, styles.qtyRowLocked]}>
          <View style={styles.qtyNameWrap}>
            <Text style={[styles.qtyName, styles.qtyNameLocked]}>{eq.name}</Text>
            <Text style={styles.qtyUnit}>{eq.unit}</Text>
            <Text style={styles.qtyLockedHint}>{t('usage.wizardCategoryRequiresActionNumber')}</Text>
          </View>
          <View style={styles.qtyControls}>
            {reusableUse ? <View style={[styles.useButton, styles.qtyBtnDisabled]}>
              <Text>{t('operations.used')}</Text>
            </View> : <>
            <View style={[styles.qtyBtn, styles.qtyBtnDisabled]}>
              <Text style={styles.qtyBtnText}>−</Text>
            </View>
            <TextInput
              style={[styles.qtyInput, styles.qtyInputLocked]}
              keyboardType="number-pad"
              value=""
              placeholder="0"
              placeholderTextColor="#CBD5E1"
              editable={false}
            />
            <View style={[styles.qtyBtn, styles.qtyBtnDisabled]}>
              <Text style={styles.qtyBtnText}>+</Text>
            </View>
            </>}
          </View>
        </View>
      );
    }

    return (
      <View key={eq.id}>
      <View style={styles.qtyRow}>
        {data.usageType === 'stocktaking' && (
          <TouchableOpacity
            style={[styles.checkbox, styles.qtyRowCheckbox, isChecked && styles.checkboxChecked]}
            accessibilityRole="checkbox"
            accessibilityLabel={eq.name}
            accessibilityState={{ checked: isChecked }}
            onPress={() => toggleCheckedItem(eq.id)}
          >
            {isChecked && <Text style={styles.checkmark}>✓</Text>}
          </TouchableOpacity>
        )}
        <View style={styles.qtyNameWrap}>
          <Text style={styles.qtyName}>{eq.name}</Text>
          <Text style={styles.qtyUnit}>{eq.unit}</Text>
          {sourceStoreId && (
            <Text style={styles.qtyStock}>
              {t('usage.wizardStockAvailable').replace('%s', `${stockQty} ${eq.unit}`)}
            </Text>
          )}
          {referenceStoreId && referenceEntry && (
            <Text style={styles.qtyStock}>
              {t('usage.wizardStockAvailable').replace('%s', `${referenceEntry.quantity} ${eq.unit}`)}
            </Text>
          )}
        </View>
        <View style={styles.qtyControls}>
          {reusableUse ? <TouchableOpacity
            accessibilityRole="checkbox" accessibilityLabel={`${t('operations.used')}: ${eq.name}`}
            accessibilityState={{ checked: qty > 0, disabled: stockQty <= 0 && qty === 0 }}
            aria-checked={qty > 0}
            disabled={stockQty <= 0 && qty === 0}
            style={[styles.useButton, qty > 0 && styles.useButtonSelected, stockQty <= 0 && qty === 0 && styles.qtyBtnDisabled]}
            onPress={() => setQtyFromInput(eq.id, qty > 0 ? '0' : '1', stockQty)}>
            <Text style={qty > 0 ? styles.useButtonSelectedText : styles.useButtonText}>{qty > 0 ? '✓ ' : ''}{t('operations.used')}</Text>
          </TouchableOpacity> : <>
          <TouchableOpacity
            style={[styles.qtyBtn, detailsOpen && styles.qtyBtnDisabled]}
            disabled={detailsOpen}
            onPress={() => adjustQty(eq.id, -1, sourceStoreId ? stockQty : undefined, stocktakingDisplayQty)}
          >
            <Text style={styles.qtyBtnText}>−</Text>
          </TouchableOpacity>
          <TextInput
            style={[styles.qtyInput, detailsOpen && styles.qtyInputLocked]}
            editable={!detailsOpen}
            accessibilityLabel={`${t('operations.quantity')} ${eq.name}`}
            keyboardType="number-pad"
            value={stocktakingDisplayQty === 0 ? '' : stocktakingDisplayQty.toString()}
            placeholder="0"
            placeholderTextColor="#94A3B8"
            onChangeText={v => setQtyFromInput(eq.id, v, sourceStoreId ? stockQty : undefined)}
          />
          <TouchableOpacity
            style={[
              styles.qtyBtn,
              styles.qtyBtnPlus,
              (detailsOpen || (sourceStoreId && qty >= stockQty)) && styles.qtyBtnDisabled,
            ]}
            onPress={() => adjustQty(eq.id, 1, sourceStoreId ? stockQty : undefined, stocktakingDisplayQty)}
            disabled={detailsOpen || (sourceStoreId !== null && qty >= stockQty)}
          >
            <Text style={styles.qtyBtnText}>+</Text>
          </TouchableOpacity>
          </>}
          {hasDates && <TouchableOpacity
            style={[styles.qtyBtn, detailsOpen && styles.qtyBtnPlus]}
            accessibilityRole="button" accessibilityLabel={t(detailsOpen ? 'operations.hideDates' : 'operations.showDates')} accessibilityState={{ expanded: detailsOpen }}
            onPress={() => {
              setExpandedDetails(prev => { const next = new Set(prev); if (next.has(eq.id)) next.delete(eq.id); else next.add(eq.id); return next; });
              const picked = selection[eq.id] ?? emptySelection();
              if (detailsOpen) {
                if (data.usageType === 'take' || data.usageType === 'transfer') setSelection(prev => ({ ...prev, [eq.id]: { ...picked, mode: 'fefo', allocations: [] } }));
                return;
              }
              const source = batchData?.batches.filter(b => b.equipmentId === eq.id) ?? [];
              if (data.usageType === 'add' || data.usageType === 'stocktaking') {
                const lots = picked.initialized ? picked.lots : data.usageType === 'add'
                  ? [{ id: newRequestId(), storeId: currentStoreId, equipmentId: eq.id, quantity: stocktakingDisplayQty, expiresOn: null, reviewOn: null }]
                  : source;
                setSelection(prev => ({ ...prev, [eq.id]: { ...picked, initialized: true, lots } }));
                setQtyFromInput(eq.id, String(lots.reduce((n, b) => n + b.quantity, 0)), undefined, true);
              } else {
                const allocations = picked.mode === 'manual' ? picked.allocations : fefoAllocations(source, stocktakingDisplayQty, eq);
                setSelection(prev => ({ ...prev, [eq.id]: { ...picked, mode: 'manual', allocations } }));
              }
            }}>
            <Ionicons name="time-outline" size={19} color="#2563EB" />
          </TouchableOpacity>}
        </View>
      </View>
      {hasDates && <InventoryBatchPanel
        batches={batchData?.batches.filter(b => b.equipmentId === eq.id) ?? []}
        quantity={stocktakingDisplayQty} stockQuantity={stockQty} unit={eq.unit} storeId={currentStoreId} equipmentId={eq.id}
        type={data.usageType} value={selection[eq.id] ?? emptySelection()} tracking={eq}
        hideToggle expanded={detailsOpen}
        unresolved={batchData?.positions.find(p => p.equipmentId === eq.id)?.unresolvedQuantity ?? 0}
        onChange={picked => {
          setSelection(prev => ({ ...prev, [eq.id]: picked }));
          const total = data.usageType === 'stocktaking' || data.usageType === 'add'
            ? picked.lots.reduce((n, b) => n + b.quantity, 0)
            : picked.allocations.reduce((n, a) => n + a.quantity, 0);
          setQtyFromInput(eq.id, String(total), sourceStoreId ? stockQty : undefined, true);
        }}
      />}
      </View>
    );
  };

  // ---- Store chip selector ----

  const renderStoreChips = (
    label: string,
    selectedId: string,
    onSelect: (id: string) => void,
    excludeId?: string,
  ) => (
    <>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.chipRow}>
        {stores
          .filter(s => s.id !== excludeId && (!taskId || s.id === data.toStoreId))
          .map(s => (
            <TouchableOpacity
              key={s.id}
              style={[styles.chip, selectedId === s.id && styles.chipSelected]}
              onPress={() => onSelect(s.id)}
            >
              <Text style={[styles.chipText, selectedId === s.id && styles.chipTextSelected]}>
                {s.name}
              </Text>
            </TouchableOpacity>
          ))}
      </View>
    </>
  );

  // ---- Step renders ----

  const renderStep0 = () => {
    const typeOptions: { value: UsageType; label: string; color: string }[] = [
      { value: 'take', label: t('usage.typeTake'), color: '#EF4444' },
      ...((isAdminOrManager || !batchManaged) ? [{ value: 'add' as UsageType, label: t('usage.typeAdd'), color: '#22C55E' }] : []),
      { value: 'transfer', label: t('usage.typeTransfer'), color: '#6366F1' },
      ...((isAdminOrManager || taskId) ? [{ value: 'stocktaking' as UsageType, label: t('usage.typeStocktaking'), color: '#F59E0B' }] : []),
    ];

    return (
      <View style={styles.stepContent}>
        <Text style={styles.sectionTitle}>{t('usage.wizardStepInfo')}</Text>

        <View style={styles.row2}>
          <View style={styles.col}>
            <DateTimePickerInput
              mode="date"
              label={t('usage.wizardDateLabel')}
              value={data.date}
              onChange={v => setData(d => ({ ...d, date: v }))}
              minDate={minDate}
              maxDate={maxDate}
            />
          </View>
          <View style={styles.col}>
            <DateTimePickerInput
              mode="time"
              label={t('usage.wizardTimeLabel')}
              value={data.time}
              onChange={v => setData(d => ({ ...d, time: v }))}
            />
          </View>
        </View>

        <Text style={styles.label}>{t('usage.wizardTypeLabel')}</Text>
        <View style={styles.chipRow}>
          {typeOptions.filter(opt => !taskId || opt.value === 'stocktaking').map(opt => (
            <TouchableOpacity
              key={opt.value}
              style={[
                styles.typeChip,
                data.usageType === opt.value && { backgroundColor: opt.color, borderColor: opt.color },
              ]}
              onPress={() => setData(d => ({ ...d, usageType: opt.value }))}
            >
              <Text style={[
                styles.typeChipText,
                data.usageType === opt.value && styles.typeChipTextSelected,
              ]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {(data.usageType === 'take' || data.usageType === 'transfer') &&
          renderStoreChips(
            t('usage.wizardFromStoreLabel'),
            data.fromStoreId,
            id => setData(d => ({ ...d, fromStoreId: id })),
            data.usageType === 'transfer' ? data.toStoreId : undefined,
          )}

        {(data.usageType === 'add' || data.usageType === 'transfer') &&
          renderStoreChips(
            t('usage.wizardToStoreLabel'),
            data.toStoreId,
            id => setData(d => ({ ...d, toStoreId: id })),
            data.usageType === 'transfer' ? data.fromStoreId : undefined,
          )}

        {data.usageType === 'stocktaking' &&
          renderStoreChips(
            t('usage.wizardStoreStocktakingLabel'),
            data.toStoreId,
            id => setData(d => ({ ...d, toStoreId: id })),
          )}

        <Text style={styles.label}>{t('usage.wizardActionNumberLabel')}</Text>
        <TextInput
          style={styles.input}
          value={data.actionNumber}
          onChangeText={v => setData(d => ({ ...d, actionNumber: v }))}
          placeholder={t('usage.wizardActionNumberPlaceholder')}
          placeholderTextColor="#94A3B8"
        />
        {anyRootCategoryRequiresActionNumber && (
          <View style={styles.actionNumberInfoBox}>
            <Text style={styles.actionNumberInfoText}>ℹ️ {t('usage.wizardActionNumberInfo')}</Text>
          </View>
        )}

        <Text style={styles.label}>{t('usage.wizardDescriptionLabel')}</Text>
        <TextInput
          style={[styles.input, styles.multilineInput]}
          value={data.description}
          onChangeText={v => setData(d => ({ ...d, description: v }))}
          placeholder={t('usage.wizardDescriptionPlaceholder')}
          placeholderTextColor="#94A3B8"
          multiline
          numberOfLines={3}
        />
      </View>
    );
  };

  const renderStep1 = () => (
    <View style={styles.stepContent}>
      <Text style={styles.sectionTitle}>{t('usage.wizardStepCategories')}</Text>
      <Text style={styles.hint}>{t('usage.wizardSelectCategories')}</Text>
      {rootCategories.map(cat => {
        const checked = data.selectedCategoryIds.includes(cat.id);
        const locked = data.usageType === 'take' && !!cat.requiresActionNumber && !hasValidActionNumber;
        return (
          <TouchableOpacity
            key={cat.id}
            style={[styles.catRow, checked && !locked && styles.catRowSelected, locked && styles.catRowLocked]}
            onPress={() => !locked && toggleCategory(cat.id)}
            activeOpacity={locked ? 1 : 0.7}
          >
            <View style={[styles.checkbox, checked && !locked && styles.checkboxChecked, locked && styles.checkboxLocked]}>
              {checked && !locked && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <View style={styles.catNameWrap}>
              <Text style={[styles.catName, checked && !locked && styles.catNameSelected, locked && styles.catNameLocked]}>
                {cat.name}
              </Text>
              {locked && (
                <Text style={styles.catLockedHint}>{t('usage.wizardCategoryRequiresActionNumber')}</Text>
              )}
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderEquipmentStep = () => {
    const subcategories = currentCategory
      ? categories.filter(c => c.parentId === currentCategory.id)
      : [];
    const hasSubcategories = subcategories.length > 0;

    /** Returns true when the equipment should be locked in this step. */
    const isEqLocked = (eq: Equipment): boolean =>
      data.usageType === 'take' && !hasValidActionNumber && isCategoryChainLocked(eq.categoryId, categories);

    const renderCategorizedContent = () => {
      if (!currentCategory) return null;

      // Equipment directly in the current category (not in subcategories)
      const directEquipment = currentEquipment.filter(
        e => e.categoryId === currentCategory.id,
      );

      return (
        <>
          {directEquipment.map(eq => renderQtyRow(eq, isEqLocked(eq)))}
          {subcategories.map(sub => {
            const subIds = getDescendantIds(sub.id, categories);
            const subEquipment = currentEquipment.filter(e => subIds.includes(e.categoryId));
            if (subEquipment.length === 0) return null;
            const subLocked = data.usageType === 'take' && !hasValidActionNumber && !!sub.requiresActionNumber;
            return (
              <View key={sub.id}>
                <View style={styles.subcategoryHeaderRow}>
                  <Text style={[styles.subcategoryHeader, subLocked && styles.subcategoryHeaderLocked]}>
                    {sub.name}
                  </Text>
                  {subLocked && (
                    <Text style={styles.catLockedHint}>{t('usage.wizardCategoryRequiresActionNumber')}</Text>
                  )}
                </View>
                {subEquipment.map(eq => renderQtyRow(eq, isEqLocked(eq)))}
              </View>
            );
          })}
        </>
      );
    };

    return (
      <View style={styles.stepContent}>
        <View style={styles.equipmentStepHeader}>
          <Text style={styles.sectionTitle}>{currentCategory?.name}</Text>
          {hasSubcategories && (
            <View style={styles.viewToggle}>
              <TouchableOpacity
                style={[styles.viewToggleBtn, viewMode === 'full' && styles.viewToggleBtnActive]}
                onPress={() => setViewMode('full')}
              >
                <Text style={[styles.viewToggleBtnText, viewMode === 'full' && styles.viewToggleBtnTextActive]}>
                  {t('usage.wizardViewFull')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.viewToggleBtn, viewMode === 'categorized' && styles.viewToggleBtnActive]}
                onPress={() => setViewMode('categorized')}
              >
                <Text style={[styles.viewToggleBtnText, viewMode === 'categorized' && styles.viewToggleBtnTextActive]}>
                  {t('usage.wizardViewCategorized')}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        {data.usageType === 'stocktaking' && (
          <Text style={styles.hint}>{t('usage.wizardStocktakingHint')}</Text>
        )}
        {currentEquipment.length === 0 ? (
          <Text style={styles.hint}>{t('usage.wizardNoEquipment')}</Text>
        ) : viewMode === 'categorized' && hasSubcategories ? (
          renderCategorizedContent()
        ) : (
          currentEquipment.map(eq => renderQtyRow(eq, isEqLocked(eq)))
        )}
      </View>
    );
  };

  const renderReview = () => {
    const displayItems = data.usageType === 'stocktaking' ? stocktakingItems : usedItems;
    return (
      <View style={styles.stepContent}>
        <Text style={styles.sectionTitle}>{t('usage.wizardReviewItems')}</Text>

        {displayItems.length === 0 ? (
          <Text style={styles.hint}>{t('usage.wizardNoItemsAdded')}</Text>
        ) : (
          displayItems.map(({ equipment: eq }) => renderQtyRow(eq))
        )}

        <Text style={[styles.label, { marginTop: 24 }]}>{t('usage.wizardAddItem')}</Text>
        <TextInput
          style={styles.input}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={t('usage.wizardSearchPlaceholder')}
          placeholderTextColor="#94A3B8"
        />
        {searchResults.map(eq => (
          <TouchableOpacity
            key={eq.id}
            style={styles.searchResult}
            onPress={() => {
              if (data.usageType === 'stocktaking') {
                toggleCheckedItem(eq.id);
              } else {
                adjustQty(eq.id, 1);
              }
              setSearchQuery('');
            }}
          >
            <Text style={styles.searchResultName}>{eq.name}</Text>
            <Text style={styles.searchResultUnit}>{eq.unit}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const renderSummary = () => {
    const fromStoreName = stores.find(s => s.id === data.fromStoreId)?.name ?? data.fromStoreId;
    const toStoreName = stores.find(s => s.id === data.toStoreId)?.name ?? data.toStoreId;

    const typeLabels: Record<UsageType, string> = {
      take: t('usage.typeTake'),
      add: t('usage.typeAdd'),
      transfer: t('usage.typeTransfer'),
      stocktaking: t('usage.typeStocktaking'),
    };

    const summaryItems = data.usageType === 'stocktaking' ? stocktakingItems : usedItems;

    return (
      <View style={styles.stepContent}>
        <Text style={styles.sectionTitle}>{t('usage.wizardStepSummary')}</Text>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>{t('usage.wizardSummaryDate')}</Text>
          <Text style={styles.summaryValue}>{`${data.date}  ${data.time}`}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>{t('usage.wizardSummaryType')}</Text>
          <Text style={styles.summaryValue}>{typeLabels[data.usageType]}</Text>
        </View>
        {(data.usageType === 'take' || data.usageType === 'transfer') && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('usage.wizardSummaryFromStore')}</Text>
            <Text style={styles.summaryValue}>{fromStoreName || '—'}</Text>
          </View>
        )}
        {(data.usageType === 'add' || data.usageType === 'transfer') && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('usage.wizardSummaryToStore')}</Text>
            <Text style={styles.summaryValue}>{toStoreName || '—'}</Text>
          </View>
        )}
        {data.usageType === 'stocktaking' && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('usage.wizardStoreStocktakingLabel')}</Text>
            <Text style={styles.summaryValue}>{toStoreName || '—'}</Text>
          </View>
        )}
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>{t('usage.wizardSummaryAction')}</Text>
          <Text style={styles.summaryValue}>{data.actionNumber || '—'}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>{t('usage.wizardSummaryDescription')}</Text>
          <Text style={styles.summaryValue}>{data.description || '—'}</Text>
        </View>

        <View style={[styles.summaryRow, { flexDirection: 'column', alignItems: 'flex-start' }]}>
          <Text style={styles.summaryLabel}>{t('usage.wizardSummaryItems')}</Text>
          {summaryItems.length === 0 ? (
            <Text style={styles.summaryValue}>—</Text>
          ) : (
            summaryItems.map(({ equipment: eq, quantity }) => (
              <Text key={eq.id} style={styles.summaryItem}>
                {`• ${eq.name}: ${data.usageType === 'take' && eq.itemType === 'reusable' ? t('operations.used') : `${quantity} ${eq.unit}`}`}
              </Text>
            ))
          )}
        </View>
      </View>
    );
  };

  const renderStepContent = () => {
    if (step === 0) return renderStep0();
    if (step === 1) return renderStep1();
    if (step >= catStepStart && step < reviewStep) return renderEquipmentStep();
    if (step === reviewStep) return renderReview();
    return renderSummary();
  };

  // ---- Progress bar ----
  const progressPercent = ((step + 1) / totalSteps) * 100;

  // ---- Next button label & disabled state ----
  const nextLabel =
    step === summaryStep ? t('usage.wizardConfirm') : t('usage.wizardNext');
  const nextDisabled = saving || taskLoading || !!taskError || (batchManaged && (batchLoading || !!batchError || !batchData)) || (step === 0 && !step0Valid());

  const isDesktop = windowWidth >= 1024;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={() => { if (!saving) onClose(); }}>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={[styles.innerContent, isDesktop && styles.innerContentDesktop]}>
          {/* ── Header ── */}
          <View style={styles.header}>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose} disabled={saving}>
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
            <View style={styles.headerCenter}>
              <Text style={styles.stepTitle} numberOfLines={1}>{getStepTitle()}</Text>
              <Text style={styles.stepCount}>{step + 1} / {totalSteps}</Text>
            </View>
            {/* Spacer to balance the close button */}
            <View style={styles.closeBtn} />
          </View>

          {/* ── Progress bar ── */}
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${progressPercent}%` as unknown as number }]} />
          </View>

          {/* ── Content ── */}
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {batchLoading || taskLoading ? <ActivityIndicator /> : null}
            {!!(batchError || taskError) && <Text style={styles.hint}>{batchError || taskError}</Text>}
            {!!taskId && <Text style={styles.hint}>{t('operations.taskWizardHint')}</Text>}
            {currentStoreId && !batchManaged && batchData && isAdminOrManager && <StoreBatchSettings data={batchData} storeId={currentStoreId} admin={!!isAdmin} onRefresh={async () => setBatchData(await dashboard(currentStoreId))} />}
            {!!taskId && data.usageType === 'stocktaking' && batchManaged && !batchLoading && !taskLoading && <View style={styles.chipRow}>
              <TouchableOpacity disabled={saving} style={styles.chip} onPress={handleSaveDraft}><Text>{t('operations.saveDraft')}</Text></TouchableOpacity>
            </View>}
            {renderStepContent()}
          </ScrollView>

          {/* ── Footer ── */}
          <View style={styles.footer}>
            {step > 0 && (
              <TouchableOpacity style={styles.prevBtn} onPress={handlePrev}>
                <Text style={styles.prevBtnText}>{t('usage.wizardBack')}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.nextBtn, nextDisabled && styles.nextBtnDisabled, step === 0 && styles.nextBtnFull]}
              onPress={handleNext}
              disabled={nextDisabled}
            >
              {saving ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <Text style={styles.nextBtnText}>{nextLabel}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  useButton: { minWidth: 88, minHeight: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: '#2563EB', borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  useButtonSelected: { backgroundColor: '#2563EB' },
  useButtonText: { color: '#2563EB', fontWeight: '600' },
  useButtonSelectedText: { color: '#FFF', fontWeight: '600' },
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1 },
  innerContentDesktop: { maxWidth: 800, width: '100%', alignSelf: 'center' },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'ios' ? 56 : 16,
    paddingBottom: 12,
    backgroundColor: '#FFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  headerCenter: { flex: 1, alignItems: 'center' },
  stepTitle: { fontSize: 16, fontWeight: '700', color: '#1E293B' },
  stepCount: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  closeBtn: { width: 36, alignItems: 'center' },
  closeBtnText: { fontSize: 18, color: '#64748B' },

  // Progress
  progressTrack: { height: 4, backgroundColor: '#E2E8F0' },
  progressFill: { height: 4, backgroundColor: '#2563EB' },

  // Content
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 32 },
  stepContent: { padding: 20 },
  sectionTitle: { fontSize: 20, fontWeight: '700', color: '#1E293B', marginBottom: 20 },
  label: { fontSize: 13, fontWeight: '600', color: '#64748B', marginBottom: 6 },
  hint: { fontSize: 14, color: '#94A3B8', marginBottom: 16 },

  // Inputs
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#FFF',
    marginBottom: 16,
  },
  multilineInput: { height: 90, textAlignVertical: 'top' },

  row2: { flexDirection: 'row', gap: 12 },
  col: { flex: 1 },

  // Store chips
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
  },
  chipSelected: { backgroundColor: '#EFF6FF', borderColor: '#2563EB' },
  chipText: { fontSize: 14, color: '#475569' },
  chipTextSelected: { color: '#2563EB', fontWeight: '700' },

  // Type chips
  typeChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
  },
  typeChipText: { fontSize: 14, fontWeight: '600', color: '#475569' },
  typeChipTextSelected: { color: '#FFF' },

  // Category rows
  catRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    marginBottom: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFF',
    gap: 12,
  },
  catRowSelected: { backgroundColor: '#EFF6FF', borderColor: '#2563EB' },
  catRowLocked: { backgroundColor: '#F8FAFC', borderColor: '#E2E8F0', opacity: 0.6 },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  checkboxLocked: { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' },
  checkmark: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  qtyRowCheckbox: { marginRight: 10 },
  catNameWrap: { flex: 1 },
  catName: { fontSize: 15, color: '#475569' },
  catNameSelected: { color: '#1E40AF', fontWeight: '600' },
  catNameLocked: { color: '#94A3B8' },
  catLockedHint: { fontSize: 12, color: '#94A3B8', marginTop: 2 },

  // Action number info box
  actionNumberInfoBox: {
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    padding: 10,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#BFDBFE',
  },
  actionNumberInfoText: { fontSize: 13, color: '#1D4ED8' },

  // Equipment step header
  equipmentStepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },

  // View toggle (full / categorized)
  viewToggle: {
    flexDirection: 'row',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  viewToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#F8FAFC',
  },
  viewToggleBtnActive: { backgroundColor: '#2563EB' },
  viewToggleBtnText: { fontSize: 12, fontWeight: '600', color: '#64748B' },
  viewToggleBtnTextActive: { color: '#FFF' },

  // Subcategory header
  subcategoryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#DBEAFE',
    paddingVertical: 6,
    gap: 8,
  },
  subcategoryHeader: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2563EB',
  },
  subcategoryHeaderLocked: { color: '#94A3B8' },

  // Quantity rows
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  qtyRowLocked: { opacity: 0.5 },
  qtyNameWrap: { flex: 1 },
  qtyName: { fontSize: 14, color: '#1E293B', fontWeight: '500' },
  qtyNameLocked: { color: '#94A3B8' },
  qtyLockedHint: { fontSize: 11, color: '#94A3B8', marginTop: 2 },
  qtyUnit: { fontSize: 12, color: '#94A3B8' },
  qtyStock: { fontSize: 11, color: '#64748B', marginTop: 2 },
  qtyControls: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  qtyBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#F8FAFC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyBtnPlus: { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' },
  qtyBtnDisabled: { backgroundColor: '#F1F5F9', borderColor: '#E2E8F0', opacity: 0.5 },
  qtyBtnText: { fontSize: 20, color: '#374151', lineHeight: 24 },
  qtyInput: {
    width: 48,
    height: 34,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    textAlign: 'center',
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#FFF',
  },
  qtyInputLocked: { backgroundColor: '#F8FAFC', color: '#94A3B8' },

  // Search results
  searchResult: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 6,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 8,
  },
  searchResultName: { flex: 1, fontSize: 14, color: '#1E293B' },
  searchResultUnit: { fontSize: 12, color: '#94A3B8' },

  // Summary
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    gap: 12,
  },
  summaryLabel: {
    width: 120,
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
  },
  summaryValue: { flex: 1, fontSize: 15, color: '#1E293B' },
  summaryItem: { fontSize: 15, color: '#1E293B', marginTop: 4 },

  // Footer navigation
  footer: {
    flexDirection: 'row',
    padding: 16,
    paddingBottom: Platform.OS === 'ios' ? 32 : 16,
    backgroundColor: '#FFF',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    gap: 12,
  },
  prevBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
  },
  prevBtnText: { fontSize: 15, fontWeight: '600', color: '#64748B' },
  nextBtn: {
    flex: 2,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: '#2563EB',
    alignItems: 'center',
  },
  nextBtnFull: { flex: 1 },
  nextBtnDisabled: { backgroundColor: '#93C5FD' },
  nextBtnText: { fontSize: 15, fontWeight: '700', color: '#FFF' },
});
